import React, { useState } from 'react';
import axios from 'axios';
import { plans } from '../mock';
import { Check, Trophy, Info, Loader2 } from 'lucide-react';

export default function Pricing() {
  const [activePlan, setActivePlan] = useState(null);
  const [checkoutStatus, setCheckoutStatus] = useState({ type: '', message: '' });
  const apiBase = window.__APP_CONFIG__?.apiBase || process.env.REACT_APP_API_URL || '';

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const existing = document.querySelector('script[data-razorpay-checkout]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => reject(new Error('Unable to load Razorpay Checkout.')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.dataset.razorpayCheckout = 'true';
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Unable to load Razorpay Checkout.'));
      document.body.appendChild(script);
    });

  const startCheckout = async (plan) => {
    setActivePlan(plan.id);
    setCheckoutStatus({ type: '', message: '' });
    try {
      await loadRazorpayScript();
      const { data: order } = await axios.post(`${apiBase}/api/razorpay-order`, {
        product_id: plan.id,
      });

      const checkout = new window.Razorpay({
        key: order.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'MindToMaster',
        description: plan.name,
        order_id: order.order_id,
        theme: { color: '#10b981' },
        retry: { enabled: true },
        handler: async (paymentResponse) => {
          try {
            await axios.post(`${apiBase}/api/razorpay-confirm`, {
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            });
            setCheckoutStatus({
              type: 'success',
              message: 'Payment verified. Your login credentials will be sent by email and SMS shortly.',
            });
          } catch (error) {
            setCheckoutStatus({
              type: 'error',
              message: error?.response?.data?.detail || 'Payment completed, but verification is still pending. Contact support with your payment ID.',
            });
          } finally {
            setActivePlan(null);
          }
        },
        modal: {
          ondismiss: () => {
            setCheckoutStatus({ type: 'error', message: 'Payment cancelled. No charge was made.' });
            setActivePlan(null);
          },
        },
      });
      checkout.on('payment.failed', (response) => {
        setCheckoutStatus({
          type: 'error',
          message: response?.error?.description || 'Payment failed. Please try again.',
        });
        setActivePlan(null);
      });
      checkout.open();
    } catch (error) {
      setCheckoutStatus({
        type: 'error',
        message: error?.response?.data?.detail || error?.message || 'Unable to start checkout. Please try again.',
      });
      setActivePlan(null);
    }
  };

  return (
    <section id="pricing" className="py-20">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rose-500/30 bg-rose-500/5 text-rose-300 text-xs font-semibold">
            <Trophy size={12} /> Special Launch Offer
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            Choose Your Success Path
          </h2>
          <p className="mt-3 text-slate-400">Unbeatable prices, premium quality</p>
        </div>

        {checkoutStatus.message && (
          <div
            role="status"
            className={`mb-6 rounded-xl border p-4 text-sm ${
              checkoutStatus.type === 'success'
                ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100'
                : 'border-rose-400/40 bg-rose-500/10 text-rose-100'
            }`}
          >
            {checkoutStatus.message}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`relative overflow-hidden rounded-2xl border p-8 transition-all ${
                p.popular
                  ? 'border-emerald-500/60 bg-gradient-to-b from-emerald-500/5 to-transparent'
                  : 'border-white/10 bg-[#0f1725]'
              }`}
            >
              {p.popular && (
                <div className="absolute -top-px -right-px overflow-hidden w-40 h-40 pointer-events-none">
                  <div className="absolute top-6 -right-12 rotate-45 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 text-[10px] font-extrabold tracking-wider text-center py-1.5 w-48 shadow-lg">
                    BEST VALUE
                  </div>
                </div>
              )}

              <h3 className="text-white font-extrabold text-2xl">{p.name}</h3>
              <p className="mt-1 text-slate-400 text-sm">{p.subtitle}</p>

              <div className="mt-6 flex items-end gap-3">
                <span className="text-5xl font-extrabold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  ₹{p.price}
                </span>
                <span className="text-slate-500 line-through text-lg mb-1">₹{p.oldPrice}</span>
              </div>

              <div className="mt-3 flex items-center gap-3 text-sm">
                <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 font-semibold text-xs">
                  {p.discount}
                </span>
              </div>

              <div className="mt-5 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex justify-between text-xs">
                  <span className="text-amber-300 font-semibold">Only {p.slotsLeft} slots left!</span>
                  <span className="text-slate-400">{p.slotsLeft}/{p.totalSlots}</span>
                </div>
                <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-rose-400"
                    style={{ width: `${(p.slotsLeft / p.totalSlots) * 100}%` }}
                  />
                </div>
              </div>

              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <Check size={12} className="text-emerald-400" />
                    </span>
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => startCheckout(p)}
                disabled={activePlan !== null}
                className={`mt-8 w-full py-3 rounded-md font-semibold transition-all ${
                  p.popular
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-900 shadow-lg shadow-emerald-500/25'
                    : 'bg-white/10 hover:bg-white/15 text-white border border-white/10'
                }`}
              >
                {activePlan === p.id ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Opening Razorpay…
                  </span>
                ) : (
                  `Buy ${p.name} with Razorpay`
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-12 rounded-2xl border border-white/10 bg-[#0f1725] overflow-hidden">
          <div className="px-6 py-5 border-b border-white/10">
            <h3 className="text-white font-bold text-xl">Compare Plans Side-by-Side</h3>
            <p className="text-slate-400 text-sm mt-1">See exactly what you get with each pass</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="py-4 px-6 text-slate-300 font-semibold">Features</th>
                  <th className="py-4 px-6 text-center">
                    <div className="text-white font-bold">Basic Pass</div>
                    <div className="text-emerald-400 font-semibold">&#8377;499</div>
                  </th>
                  <th className="py-4 px-6 text-center bg-emerald-500/5">
                    <div className="text-white font-bold flex items-center justify-center gap-1">
                      Premium Bundle <Trophy size={12} className="text-amber-400" />
                    </div>
                    <div className="text-emerald-400 font-semibold">&#8377;999</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { f: 'Exam Coverage', a: '1 Exam', b: '50+ Exams' },
                  { f: 'Test Series Access', a: '1 Series', b: 'Unlimited' },
                  { f: 'Full-length Mock Tests', a: true, b: true },
                  { f: 'Sectional Tests', a: true, b: true },
                  { f: 'Topic-wise Tests + PYQ Vault', a: true, b: true },
                  { f: 'AI-Powered Rank Predictor', a: true, b: true },
                  { f: 'Deep Analytics & Explanations', a: true, b: true },
                  { f: 'Cross-Exam Pattern Learning', a: false, b: true },
                  { f: 'All Government Exams Coverage', a: false, b: true },
                  { f: 'Priority Expert Support', a: false, b: true },
                  { f: 'Free Lifetime Updates', a: false, b: true },
                  { f: 'Validity', a: '1 Year', b: '1 Year' },
                ].map((row, i) => (
                  <tr
                    key={row.f}
                    className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.015]' : ''}`}
                  >
                    <td className="py-3.5 px-6 text-slate-300">{row.f}</td>
                    <td className="py-3.5 px-6 text-center">
                      {typeof row.a === 'boolean' ? (
                        row.a ? (
                          <Check size={16} className="text-emerald-400 mx-auto" />
                        ) : (
                          <span className="text-slate-600">&mdash;</span>
                        )
                      ) : (
                        <span className="text-slate-300">{row.a}</span>
                      )}
                    </td>
                    <td className="py-3.5 px-6 text-center bg-emerald-500/[0.03]">
                      {typeof row.b === 'boolean' ? (
                        row.b ? (
                          <Check size={16} className="text-emerald-400 mx-auto" />
                        ) : (
                          <span className="text-slate-600">&mdash;</span>
                        )
                      ) : (
                        <span className="text-emerald-300 font-semibold">{row.b}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6">
          <div className="flex items-start gap-3">
            <Info className="text-emerald-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-white font-bold">Important: Exam Selection After Purchase</h4>
              <p className="mt-1 text-slate-300 text-sm leading-relaxed">
                Aapko pehle exam choose karne ki zarurat NAHI hai. Pass purchase karne ke baad aapko instant login credentials milenge, aur phir aap apni dashboard se koi bhi exam select kar sakte hain.
              </p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                {['Instant Email/SMS Login', 'Choose Exam Later', 'Full Flexibility'].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check size={16} className="text-emerald-400" /> {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
